// FamilyHealthDashboard — SFO-specific deep view of the single family's health.
// Where the multi-client `ClientHealthScores` renders a sortable grid of cards,
// this surfaces the same five-metric breakdown as a hero overview with
// inline interpretations and one-click jumps to the relevant client sub-page.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Activity, Shield, FileText, Clock, AlertTriangle, ArrowRight,
  TrendingUp, CheckCircle2, Users, Link as LinkIcon, ListTodo,
  Building2, FileCheck, Lightbulb,
} from 'lucide-react';
import HealthScoreExplainer from '@/components/dashboard/HealthScoreExplainer';

const STATUS_CONFIG = {
  excellent:        { label: 'Excellent',        color: 'text-green-700',  bg: 'bg-green-100',  hint: 'Everything is on track.' },
  good:             { label: 'Good',             color: 'text-amber-700',  bg: 'bg-amber-100',  hint: 'Solid foundation with a few items to tidy.' },
  attention_needed: { label: 'Needs Attention',  color: 'text-orange-700', bg: 'bg-orange-100', hint: 'Several dimensions are slipping — review the metrics below.' },
  critical:         { label: 'Critical',         color: 'text-red-700',    bg: 'bg-red-100',    hint: 'Multiple gaps require immediate attention.' },
};

function bandColor(v) {
  if (v >= 80) return { stroke: '#16A34A', text: 'text-green-700',  bar: 'bg-green-500',  ring: 'ring-green-200',  label: 'Strong' };
  if (v >= 60) return { stroke: '#D97706', text: 'text-amber-700',  bar: 'bg-amber-500',  ring: 'ring-amber-200',  label: 'Stable' };
  if (v >= 40) return { stroke: '#EA580C', text: 'text-orange-700', bar: 'bg-orange-500', ring: 'ring-orange-200', label: 'Watch' };
  return            { stroke: '#DC2626', text: 'text-red-700',    bar: 'bg-red-500',    ring: 'ring-red-200',    label: 'Critical' };
}

function HeroRing({ score, color, size = 140, strokeWidth = 10 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#EFEAE3" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
                strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
                className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-heading" style={{ color }}>{score}</span>
        <span className="text-[10px] uppercase tracking-wide text-[#8C8078]">out of 100</span>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, weight, description, ctaLabel, onCta, testId }) {
  const band = bandColor(value);
  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white p-4 flex flex-col gap-3" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', band.bar.replace('bg-', 'bg-') + '/15')}>
            <Icon className={cn('w-4 h-4', band.text)} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#2C2420] truncate">{label}</p>
            <p className="text-[10px] uppercase tracking-wide text-[#8C8078]">Weight {weight}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className={cn('text-2xl font-heading leading-none', band.text)}>{value}</p>
          <p className={cn('text-[10px] font-medium', band.text)}>{band.label}</p>
        </div>
      </div>
      <div className="h-1.5 bg-[#F5F3F1] rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all duration-500', band.bar)} style={{ width: `${value}%` }} />
      </div>
      <p className="text-xs text-[#5D524D] leading-relaxed">{description}</p>
      {ctaLabel && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onCta}
          className="self-start h-8 px-2 text-xs text-[#4A3B32] hover:text-[#2C2420] hover:bg-[#F5F3F0]"
          data-testid={`${testId}-cta`}
        >
          {ctaLabel} <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
}

function buildInterpretations(b, d) {
  return {
    data: b.dataCompleteness >= 80
      ? 'All foundational fields complete across individuals, entities and adviser links.'
      : b.dataCompleteness >= 60
        ? 'Most data captured. A few individuals or entities are missing key fields.'
        : b.dataCompleteness >= 40
          ? 'Significant data gaps — household structure or entity details incomplete.'
          : 'Core household data is missing. Start by capturing individuals and entities.',
    compliance: b.complianceScore >= 80
      ? 'KYC and review cycles are current. No expiring authorities.'
      : b.complianceScore >= 60
        ? 'Compliance broadly current. Schedule the next review cycle.'
        : b.complianceScore >= 40
          ? 'Some compliance tasks open or reviews approaching expiry.'
          : `${d.overdueTasks || 0} overdue compliance items detected. Action recommended.`,
    tasks: b.taskHealth >= 80
      ? 'Workflow is on time across the family.'
      : b.taskHealth >= 60
        ? 'A handful of items slipping past due dates.'
        : b.taskHealth >= 40
          ? `${d.overdueTasks || 0} overdue tasks need owners or rescheduling.`
          : `${d.overdueTasks || 0} overdue tasks blocking the workflow.`,
    links: b.linkFreshness >= 80
      ? 'All source links verified recently.'
      : b.linkFreshness >= 60
        ? 'Most links healthy — a few have not been verified this quarter.'
        : b.linkFreshness >= 40
          ? `Only ${d.links || 0} active source links. Index more documents/portfolios.`
          : 'Source link coverage is thin — index documents and portfolios.',
    activity: b.activityRecency >= 80
      ? 'Steady activity across advisers and clients.'
      : b.activityRecency >= 60
        ? 'Activity has slowed in the last fortnight.'
        : b.activityRecency >= 40
          ? 'No meaningful updates in the last 30 days.'
          : 'No recent activity on file. Log a touchpoint or review.',
  };
}

function buildRecommendations(b, d) {
  const items = [];
  if (b.dataCompleteness < 60) items.push({
    icon: Users,
    text: `Capture missing individuals (${d.individuals || 0} on file) and entities (${d.entities || 0} on file).`,
    cta: 'individuals',
  });
  if (b.complianceScore < 60) items.push({
    icon: Shield,
    text: 'Run a compliance review — schedule KYC refreshes and resolve open items.',
    cta: 'governance',
  });
  if (b.taskHealth < 70 || (d.overdueTasks || 0) > 0) items.push({
    icon: ListTodo,
    text: `Clear the ${d.overdueTasks || 0} overdue task${(d.overdueTasks || 0) === 1 ? '' : 's'} or reassign owners.`,
    cta: 'workflow',
  });
  if (b.linkFreshness < 60) items.push({
    icon: LinkIcon,
    text: 'Verify or add source links so the audit trail stays connected to original systems.',
    cta: 'links',
  });
  if (b.activityRecency < 60) items.push({
    icon: Activity,
    text: 'Log a recent touchpoint (meeting, call, review) to reflect current engagement.',
    cta: 'activity',
  });
  if (!items.length) items.push({
    icon: CheckCircle2,
    text: 'No critical gaps detected. Maintain the current cadence and review next quarter.',
    cta: null,
  });
  return items;
}

export default function FamilyHealthDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await api.getHealthScores(token);
        setData(res.data);
      } catch (e) {
        console.error('FamilyHealthDashboard fetch failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="family-health-loading">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const family = data?.scores?.[0];
  if (!family) {
    return (
      <div className="rounded-xl border border-dashed border-[#E6E0D9] p-10 text-center" data-testid="family-health-empty">
        <Users className="w-8 h-8 mx-auto text-[#8C8078] mb-2" />
        <p className="text-sm text-[#5D524D]">No family on file yet. Create your family group from the Clients tab.</p>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[family.status] || STATUS_CONFIG.critical;
  const b = family.breakdown;
  const d = family.details || {};
  const notes = buildInterpretations(b, d);
  const recommendations = buildRecommendations(b, d);

  const goTo = (sub) => navigate(`/app/clients/${family.clientId}/${sub}`);

  return (
    <div className="space-y-6" data-testid="family-health-dashboard">
      {/* Hero card */}
      <div className="rounded-2xl border border-[#E6E0D9] bg-gradient-to-br from-white to-[#FAF7F3] p-6 sm:p-8" data-testid="family-health-hero">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
          <HeroRing score={family.score} color={family.color} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="font-heading text-2xl text-[#2C2420]" data-testid="family-health-name">{family.clientName}</h2>
              <span className={cn('text-xs px-2.5 py-1 rounded-full font-medium', cfg.bg, cfg.color)} data-testid="family-health-status">
                {cfg.label}
              </span>
              <HealthScoreExplainer size="sm" label="What is this score?" />
            </div>
            <p className="text-sm text-[#5D524D] mb-4 max-w-xl">{cfg.hint}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="family-health-details">
              <DetailStat icon={Users}      label="Individuals"     value={d.individuals    || 0} onClick={() => goTo('individuals')} />
              <DetailStat icon={Building2}  label="Entities"        value={d.entities       || 0} onClick={() => goTo('entities')} />
              <DetailStat icon={LinkIcon}   label="Source links"    value={d.links          || 0} onClick={() => goTo('links')} />
              <DetailStat icon={FileCheck}  label="Advice docs"     value={d.adviceDocs     || 0} onClick={() => goTo('advice')} />
              <DetailStat icon={ListTodo}   label="Active tasks"    value={d.activeTasks    || 0} onClick={() => goTo('workflow')} />
              <DetailStat icon={AlertTriangle} label="Overdue tasks" value={d.overdueTasks  || 0} onClick={() => goTo('workflow')} highlight={(d.overdueTasks || 0) > 0} />
              <DetailStat icon={Shield}     label="Governance docs" value={d.governanceDocs || 0} onClick={() => goTo('governance')} />
              <DetailStat icon={Users}      label="Advisers"        value={d.advisers       || 0} onClick={() => goTo('advisers')} />
            </div>
          </div>
          <div className="hidden lg:block w-px h-32 bg-[#E6E0D9]" />
          <div className="flex flex-col gap-2">
            <Button onClick={() => goTo('overview')} className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white" data-testid="family-health-open-overview">
              Open client overview <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
            <Button variant="outline" onClick={() => goTo('continuity-manual')} className="border-[#E6E0D9] text-[#2C2420]" data-testid="family-health-open-continuity">
              Continuity manual
            </Button>
          </div>
        </div>
      </div>

      {/* Metric breakdown */}
      <div>
        <h3 className="font-heading text-lg text-[#2C2420] mb-3">Health breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <MetricCard
            icon={FileText} label="Data completeness" weight="25%"
            value={b.dataCompleteness} description={notes.data}
            ctaLabel={b.dataCompleteness < 80 ? 'Review individuals & entities' : null}
            onCta={() => goTo('individuals')} testId="metric-data"
          />
          <MetricCard
            icon={Shield} label="Compliance" weight="20%"
            value={b.complianceScore} description={notes.compliance}
            ctaLabel={b.complianceScore < 80 ? 'Open governance' : null}
            onCta={() => goTo('governance')} testId="metric-compliance"
          />
          <MetricCard
            icon={CheckCircle2} label="Task health" weight="20%"
            value={b.taskHealth} description={notes.tasks}
            ctaLabel={b.taskHealth < 80 ? 'Open workflow' : null}
            onCta={() => goTo('workflow')} testId="metric-tasks"
          />
          <MetricCard
            icon={TrendingUp} label="Link freshness" weight="15%"
            value={b.linkFreshness} description={notes.links}
            ctaLabel={b.linkFreshness < 80 ? 'Open source links' : null}
            onCta={() => goTo('links')} testId="metric-links"
          />
          <MetricCard
            icon={Activity} label="Activity recency" weight="20%"
            value={b.activityRecency} description={notes.activity}
            ctaLabel={b.activityRecency < 80 ? 'Open activity log' : null}
            onCta={() => goTo('activity')} testId="metric-activity"
          />
        </div>
      </div>

      {/* Recommendations */}
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-5" data-testid="family-health-recommendations">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-4 h-4 text-[#A86A3A]" />
          <h3 className="font-heading text-lg text-[#2C2420]">Recommended next actions</h3>
        </div>
        <ul className="space-y-2">
          {recommendations.map((r, i) => {
            const Icon = r.icon;
            return (
              <li key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[#F0EAE3] hover:border-[#E6E0D9] bg-[#FCFAF7]" data-testid={`recommendation-${i}`}>
                <Icon className="w-4 h-4 text-[#A86A3A] mt-0.5 shrink-0" />
                <p className="text-sm text-[#2C2420] flex-1 leading-relaxed">{r.text}</p>
                {r.cta && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => goTo(r.cta)}
                    className="h-7 text-xs border-[#E6E0D9] text-[#4A3B32] shrink-0"
                    data-testid={`recommendation-${i}-cta`}
                  >
                    Resolve <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        <p className="text-[11px] text-[#8C8078] mt-3 flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Scores update automatically as you capture data, log activity, and complete tasks.
        </p>
      </div>
    </div>
  );
}

function DetailStat({ icon: Icon, label, value, onClick, highlight }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-lg border p-3 text-left transition-all hover:border-[#A86A3A]/40 hover:shadow-sm',
        highlight ? 'border-red-200 bg-red-50/40' : 'border-[#E6E0D9] bg-white'
      )}
      data-testid={`detail-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={cn('w-3.5 h-3.5', highlight ? 'text-red-500' : 'text-[#8C8078]')} />
        <span className="text-[10px] uppercase tracking-wide text-[#8C8078]">{label}</span>
      </div>
      <p className={cn('text-xl font-heading leading-none', highlight ? 'text-red-600' : 'text-[#2C2420]')}>{value}</p>
    </button>
  );
}
